import { ValidationType } from '@blumtechgroup/blum-ui-utils/dist/cjs/types';
import {
  capitaliseFirstOnly,
} from '@blumtechgroup/blum-ui-utils/dist/cjs/utils/stringUtils';

// Define the structure for a nested question that appears based on yes/no response
export interface NestedQuestion {
  id: string;
  text: string;
  yesFollowUps?: NestedQuestion[]; // Array of follow-up questions if user answers Yes
  noFollowUps?: NestedQuestion[];  // Array of follow-up questions if user answers No
  yesInfoMessage?: string;     // Informative message to display if user answers Yes
  noInfoMessage?: string;      // Informative message to display if user answers No
}

// Main question structure
export interface Question {
  id: string;
  text: string;
  yesFollowUps?: NestedQuestion[]; // Array of follow-up questions if user answers Yes
  noFollowUps?: NestedQuestion[];  // Array of follow-up questions if user answers No
  yesInfoMessage?: string;     // Informative message to display if user answers Yes
  noInfoMessage?: string;      // Informative message to display if user answers No
}

// Questionnaire structure
export interface Questionnaire {
  id?: string;
  name: string;
  questions: Question[];
  send_to: string;
}

// Structure for storing user responses
export interface UserResponse {
  questionId: string;
  answer: 'yes' | 'no';
  responseMessage?: string;     // Message shown to the user based on their response
  nestedResponses?: UserResponse[];
}

export interface ValidationErrors {
  name: ValidationType;
  questions: ValidationType;
  send_to: ValidationType;
  questionText: { [key: string]: string | boolean };
  nestedQuestionText: { [key: string]: { [key: string]: string | boolean } };
}

export const validationErrors = {
  name: 'Please provide questionnaire name',
  questions: 'Please create at least one question',
  send_to: 'Please provide a valid email address',
  questionText: 'Please provide question text',
  nestedQuestionText: 'Please provide question text for nested question',
};

export interface Validation {
  name: (name: string) => ValidationType;
  questions: (questions: unknown[]) => ValidationType;
  send_to: (email: string) => ValidationType;
  questionText: (text: string) => ValidationType;
  nestedQuestionText: (text: string) => ValidationType;
}

export const validation: Validation = {
  name: (name) => (!name ? validationErrors.name : false),
  questions: (questions) => (questions.length === 0 ? validationErrors.questions : false),
  send_to: (email) => (!email ? validationErrors.send_to : false),
  questionText: (text) => (!text ? validationErrors.questionText : false),
  nestedQuestionText: (text) => (!text ? validationErrors.nestedQuestionText : false),
};

export enum QuestionnaireReducerAction {
  NAME,
  SEND_TO,
  ADD_QUESTION,
  DELETE_QUESTION,
  SET_QUESTION_TEXT,
  ADD_YES_FOLLOWUP,
  ADD_NO_FOLLOWUP,
  DELETE_YES_FOLLOWUP,
  DELETE_NO_FOLLOWUP,
  SET_NESTED_QUESTION_TEXT,
  SET_YES_INFO_MESSAGE,
  SET_NO_INFO_MESSAGE,
  SET_NESTED_YES_INFO_MESSAGE,
  SET_NESTED_NO_INFO_MESSAGE,
  INIT,
}

// Helper function to generate a unique ID for a nested question
const generateNestedId = (parentId: string, isYes: boolean): string => {
  return `${parentId}_${isYes ? 'yes' : 'no'}_${Date.now()}`;
};

// Helper function to find and update a nested question
export const updateNestedQuestion = (
  question: Question | NestedQuestion,
  targetId: string,
  updateFn: (q: NestedQuestion) => NestedQuestion
): Question | NestedQuestion => {
  // If this is the target question, update it
  if (question.id === targetId) {
    return updateFn(question as NestedQuestion);
  }

  // Check yes follow-up paths
  if (question.yesFollowUps && question.yesFollowUps.length > 0) {
    const updatedYesFollowUps = question.yesFollowUps.map(followUp => {
      return updateNestedQuestion(followUp, targetId, updateFn) as NestedQuestion;
    });

    // Check if any follow-up was updated
    const hasChanges = updatedYesFollowUps.some((updated, index) => {
      return updated !== question.yesFollowUps![index];
    });

    if (hasChanges) {
      // Ensure we maintain the proper types
      return {
        ...question,
        yesFollowUps: updatedYesFollowUps
      };
    }
  }

  // Check no follow-up paths
  if (question.noFollowUps && question.noFollowUps.length > 0) {
    const updatedNoFollowUps = question.noFollowUps.map(followUp => {
      return updateNestedQuestion(followUp, targetId, updateFn) as NestedQuestion;
    });

    // Check if any follow-up was updated
    const hasChanges = updatedNoFollowUps.some((updated, index) => {
      return updated !== question.noFollowUps![index];
    });

    if (hasChanges) {
      // Ensure we maintain the proper types
      return {
        ...question,
        noFollowUps: updatedNoFollowUps
      };
    }
  }

  // No changes needed
  return question;
};

// Helper function to find and delete a nested question
const deleteNestedQuestion = (
  question: Question | NestedQuestion,
  parentId: string,
  isYes: boolean,
  followUpIndex?: number
): Question | NestedQuestion => {
  // If this is the parent question, remove the appropriate follow-up
  if (question.id === parentId) {
    if (isYes) {
      if (followUpIndex !== undefined && question.yesFollowUps) {
        // Remove specific follow-up at index
        const updatedFollowUps = [...question.yesFollowUps];
        updatedFollowUps.splice(followUpIndex, 1);
        return {
          ...question,
          yesFollowUps: updatedFollowUps.length > 0 ? updatedFollowUps : undefined
        };
      } else {
        // Remove all follow-ups
        return {
          ...question,
          yesFollowUps: undefined
        };
      }
    } else {
      if (followUpIndex !== undefined && question.noFollowUps) {
        // Remove specific follow-up at index
        const updatedFollowUps = [...question.noFollowUps];
        updatedFollowUps.splice(followUpIndex, 1);
        return {
          ...question,
          noFollowUps: updatedFollowUps.length > 0 ? updatedFollowUps : undefined
        };
      } else {
        // Remove all follow-ups
        return {
          ...question,
          noFollowUps: undefined
        };
      }
    }
  }

  // Check yes follow-up paths
  if (question.yesFollowUps && question.yesFollowUps.length > 0) {
    const updatedYesFollowUps = question.yesFollowUps.map(followUp => {
      return deleteNestedQuestion(followUp, parentId, isYes, followUpIndex) as NestedQuestion;
    });

    // Check if any follow-up was updated
    const hasChanges = updatedYesFollowUps.some((updated, index) => {
      return updated !== question.yesFollowUps![index];
    });

    if (hasChanges) {
      return {
        ...question,
        yesFollowUps: updatedYesFollowUps
      };
    }
  }

  // Check no follow-up paths
  if (question.noFollowUps && question.noFollowUps.length > 0) {
    const updatedNoFollowUps = question.noFollowUps.map(followUp => {
      return deleteNestedQuestion(followUp, parentId, isYes, followUpIndex) as NestedQuestion;
    });

    // Check if any follow-up was updated
    const hasChanges = updatedNoFollowUps.some((updated, index) => {
      return updated !== question.noFollowUps![index];
    });

    if (hasChanges) {
      return {
        ...question,
        noFollowUps: updatedNoFollowUps
      };
    }
  }

  // No changes needed
  return question;
};

export const questionnaireReducer = (state: Questionnaire, action: {
  type: QuestionnaireReducerAction;
  value: any
}): Questionnaire => {
  switch (action.type) {
    case QuestionnaireReducerAction.NAME:
      return { ...state, name: capitaliseFirstOnly(action.value) };
    case QuestionnaireReducerAction.SEND_TO:
      return { ...state, send_to: action.value };

    case QuestionnaireReducerAction.ADD_QUESTION:
      const newQuestionId = `q${state.questions.length + 1}`;
      return {
        ...state,
        questions: [
          ...state.questions,
          {
            id: newQuestionId,
            text: '',
          },
        ],
      };

    case QuestionnaireReducerAction.SET_QUESTION_TEXT:
      return {
        ...state,
        questions: state.questions.map((question) => {
          if (question.id === action.value.id) {
            return {
              ...question,
              text: action.value.text,
            };
          }
          return question;
        }),
      };

    case QuestionnaireReducerAction.DELETE_QUESTION:
      return {
        ...state,
        questions: state.questions.filter((question) => question.id !== action.value),
      };

    case QuestionnaireReducerAction.ADD_YES_FOLLOWUP: {
      const { questionId } = action.value;
      const newNestedId = generateNestedId(questionId, true);

      return {
        ...state,
        questions: state.questions.map((question) => {
          if (question.id === questionId) {
            // Create a new follow-up question and add it to the array
            const newFollowUp: NestedQuestion = {
              id: newNestedId,
              text: '',
            };

            return {
              ...question,
              yesFollowUps: question.yesFollowUps ? [...question.yesFollowUps, newFollowUp] : [newFollowUp]
            };
          } else if ((question.yesFollowUps && question.yesFollowUps.length > 0) || (question.noFollowUps && question.noFollowUps.length > 0)) {
            // Check if the target is a nested question
            return updateNestedQuestion(question, questionId, (nestedQuestion) => {
              const newFollowUp: NestedQuestion = {
                id: newNestedId,
                text: '',
              };

              return {
                ...nestedQuestion,
                yesFollowUps: nestedQuestion.yesFollowUps ? [...nestedQuestion.yesFollowUps, newFollowUp] : [newFollowUp]
              };
            }) as Question;
          }
          return question;
        }),
      };
    }

    case QuestionnaireReducerAction.ADD_NO_FOLLOWUP: {
      const { questionId } = action.value;
      const newNestedId = generateNestedId(questionId, false);

      return {
        ...state,
        questions: state.questions.map((question) => {
          if (question.id === questionId) {
            // Create a new follow-up question and add it to the array
            const newFollowUp: NestedQuestion = {
              id: newNestedId,
              text: '',
            };

            return {
              ...question,
              noFollowUps: question.noFollowUps ? [...question.noFollowUps, newFollowUp] : [newFollowUp]
            };
          } else if ((question.yesFollowUps && question.yesFollowUps.length > 0) || (question.noFollowUps && question.noFollowUps.length > 0)) {
            // Check if the target is a nested question
            return updateNestedQuestion(question, questionId, (nestedQuestion) => {
              const newFollowUp: NestedQuestion = {
                id: newNestedId,
                text: '',
              };

              return {
                ...nestedQuestion,
                noFollowUps: nestedQuestion.noFollowUps ? [...nestedQuestion.noFollowUps, newFollowUp] : [newFollowUp]
              };
            }) as Question;
          }
          return question;
        }),
      };
    }

    case QuestionnaireReducerAction.SET_NESTED_QUESTION_TEXT: {
      const { questionId, text } = action.value;

      return {
        ...state,
        questions: state.questions.map((question) => {
          // Check if this is a direct child of the main question
          const yesFollowUpIndex = question.yesFollowUps?.findIndex(f => f.id === questionId);
          const noFollowUpIndex = question.noFollowUps?.findIndex(f => f.id === questionId);

          if (yesFollowUpIndex !== undefined && yesFollowUpIndex >= 0) {
            // Update the text of the nested question in the yes follow-ups array
            const updatedFollowUps = [...(question.yesFollowUps || [])];
            updatedFollowUps[yesFollowUpIndex] = {
              ...updatedFollowUps[yesFollowUpIndex],
              text,
            };

            return {
              ...question,
              yesFollowUps: updatedFollowUps,
            };
          } else if (noFollowUpIndex !== undefined && noFollowUpIndex >= 0) {
            // Update the text of the nested question in the no follow-ups array
            const updatedFollowUps = [...(question.noFollowUps || [])];
            updatedFollowUps[noFollowUpIndex] = {
              ...updatedFollowUps[noFollowUpIndex],
              text,
            };

            return {
              ...question,
              noFollowUps: updatedFollowUps,
            };
          } else if ((question.yesFollowUps && question.yesFollowUps.length > 0) || (question.noFollowUps && question.noFollowUps.length > 0)) {
            // Check if the target is a nested question within a nested question
            return updateNestedQuestion(question, questionId, (nestedQuestion) => ({
              ...nestedQuestion,
              text,
            })) as Question;
          }

          return question;
        }),
      };
    }

    case QuestionnaireReducerAction.SET_YES_INFO_MESSAGE: {
      const { questionId, message } = action.value;

      return {
        ...state,
        questions: state.questions.map((question) =>
          question.id === questionId
            ? { ...question, yesInfoMessage: message }
            : question
        ),
      };
    }

    case QuestionnaireReducerAction.SET_NO_INFO_MESSAGE: {
      const { questionId, message } = action.value;

      return {
        ...state,
        questions: state.questions.map((question) =>
          question.id === questionId
            ? { ...question, noInfoMessage: message }
            : question
        ),
      };
    }

    case QuestionnaireReducerAction.SET_NESTED_YES_INFO_MESSAGE: {
      const { questionId, message } = action.value;

      return {
        ...state,
        questions: state.questions.map((question) => {
          // Check if this is a direct child of the main question
          const yesFollowUpIndex = question.yesFollowUps?.findIndex(f => f.id === questionId);
          const noFollowUpIndex = question.noFollowUps?.findIndex(f => f.id === questionId);

          if (yesFollowUpIndex !== undefined && yesFollowUpIndex >= 0) {
            // Update the yes info message of the nested question in the yes follow-ups array
            const updatedFollowUps = [...(question.yesFollowUps || [])];
            updatedFollowUps[yesFollowUpIndex] = {
              ...updatedFollowUps[yesFollowUpIndex],
              yesInfoMessage: message,
            };

            return {
              ...question,
              yesFollowUps: updatedFollowUps,
            };
          } else if (noFollowUpIndex !== undefined && noFollowUpIndex >= 0) {
            // Update the yes info message of the nested question in the no follow-ups array
            const updatedFollowUps = [...(question.noFollowUps || [])];
            updatedFollowUps[noFollowUpIndex] = {
              ...updatedFollowUps[noFollowUpIndex],
              yesInfoMessage: message,
            };

            return {
              ...question,
              noFollowUps: updatedFollowUps,
            };
          } else if ((question.yesFollowUps && question.yesFollowUps.length > 0) || (question.noFollowUps && question.noFollowUps.length > 0)) {
            // Check if the target is a nested question within a nested question
            return updateNestedQuestion(question, questionId, (nestedQuestion) => ({
              ...nestedQuestion,
              yesInfoMessage: message,
            })) as Question;
          }

          return question;
        }),
      };
    }

    case QuestionnaireReducerAction.SET_NESTED_NO_INFO_MESSAGE: {
      const { questionId, message } = action.value;

      return {
        ...state,
        questions: state.questions.map((question) => {
          // Check if this is a direct child of the main question
          const yesFollowUpIndex = question.yesFollowUps?.findIndex(f => f.id === questionId);
          const noFollowUpIndex = question.noFollowUps?.findIndex(f => f.id === questionId);

          if (yesFollowUpIndex !== undefined && yesFollowUpIndex >= 0) {
            // Update the no info message of the nested question in the yes follow-ups array
            const updatedFollowUps = [...(question.yesFollowUps || [])];
            updatedFollowUps[yesFollowUpIndex] = {
              ...updatedFollowUps[yesFollowUpIndex],
              noInfoMessage: message,
            };

            return {
              ...question,
              yesFollowUps: updatedFollowUps,
            };
          } else if (noFollowUpIndex !== undefined && noFollowUpIndex >= 0) {
            // Update the no info message of the nested question in the no follow-ups array
            const updatedFollowUps = [...(question.noFollowUps || [])];
            updatedFollowUps[noFollowUpIndex] = {
              ...updatedFollowUps[noFollowUpIndex],
              noInfoMessage: message,
            };

            return {
              ...question,
              noFollowUps: updatedFollowUps,
            };
          } else if ((question.yesFollowUps && question.yesFollowUps.length > 0) || (question.noFollowUps && question.noFollowUps.length > 0)) {
            // Check if the target is a nested question within a nested question
            return updateNestedQuestion(question, questionId, (nestedQuestion) => ({
              ...nestedQuestion,
              noInfoMessage: message,
            })) as Question;
          }

          return question;
        }),
      };
    }

    case QuestionnaireReducerAction.DELETE_YES_FOLLOWUP: {
      const { questionId, followUpIndex } = action.value;

      return {
        ...state,
        questions: state.questions.map((question) => {
          if (question.id === questionId) {
            if (followUpIndex !== undefined && question.yesFollowUps) {
              // Remove specific follow-up at index
              const updatedFollowUps = [...question.yesFollowUps];
              updatedFollowUps.splice(followUpIndex, 1);
              return {
                ...question,
                yesFollowUps: updatedFollowUps.length > 0 ? updatedFollowUps : undefined
              };
            } else {
              // Remove all follow-ups
              return {
                ...question,
                yesFollowUps: undefined,
              };
            }
          } else if ((question.yesFollowUps && question.yesFollowUps.length > 0) || (question.noFollowUps && question.noFollowUps.length > 0)) {
            // Check if the target is a nested question
            return deleteNestedQuestion(question, questionId, true, followUpIndex) as Question;
          }
          return question;
        }),
      };
    }

    case QuestionnaireReducerAction.DELETE_NO_FOLLOWUP: {
      const { questionId, followUpIndex } = action.value;

      return {
        ...state,
        questions: state.questions.map((question) => {
          if (question.id === questionId) {
            if (followUpIndex !== undefined && question.noFollowUps) {
              // Remove specific follow-up at index
              const updatedFollowUps = [...question.noFollowUps];
              updatedFollowUps.splice(followUpIndex, 1);
              return {
                ...question,
                noFollowUps: updatedFollowUps.length > 0 ? updatedFollowUps : undefined
              };
            } else {
              // Remove all follow-ups
              return {
                ...question,
                noFollowUps: undefined,
              };
            }
          } else if ((question.yesFollowUps && question.yesFollowUps.length > 0) || (question.noFollowUps && question.noFollowUps.length > 0)) {
            // Check if the target is a nested question
            return deleteNestedQuestion(question, questionId, false, followUpIndex) as Question;
          }
          return question;
        }),
      };
    }

    case QuestionnaireReducerAction.INIT:
      return action.value;

    default:
      return state;
  }
};
